import React, { useState, useRef } from "react";
import { REGEX } from "constants/CommonConstants";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { createRoot } from "react-dom/client";
import { Input } from "reactstrap";
import { format, parse } from "date-fns";

export const isEmail = (value) => REGEX.EMAIL_REGEX.test(value);
export const isMobile = (value) => REGEX.PHONE_NO_REGEX.test(value);

export const maskEmail = (email) => {
  if (!email) return "";
  const [name, domain] = email.split("@");
  if (!domain) return email;

  return `${name.slice(0, 2)}***@${domain}`;
};

export const maskMobile = (mobile) => {
  if (!mobile) return "";
  return `******${mobile.slice(-4)}`;
};

export const ISOToHumanReadableDate = (dateStr) => {
  const date = new Date(dateStr);

  let formatted = date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Uppercase only AM/PM, not month
  formatted = formatted.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());

  return formatted; // e.g., 18 Sep 2025 09:00 AM
};

export const ISOToHumanReadableWithTimeDate = (dateStr) => {
  const date = new Date(dateStr);

  let formatted = date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Uppercase only AM/PM, not month
  formatted = formatted.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());

  return formatted; // e.g., 18 Sep 2025 09:00 AM
};

export const authorizedUser = (res) => {
  if (res?.code === 440 || res?.status === 440) {
    localStorage.clear();
    window.location.href = `/auth/login?error=Your session has expired`;
  } else {
    return;
  }
};

export const renderStars = (value, showValue = true) => {
  if (value == null) return null;

  const stars = [1, 2, 3, 4, 5].map((star) => {
    let starClass = "fa-star";
    let colorClass = "star-light";

    if (star <= Math.floor(value)) {
      starClass = "fa-star";
      colorClass = "star-yellow";
    } else if (star - 0.5 <= value) {
      starClass = "fa-star-half-alt";
      colorClass = "star-yellow";
    }

    return <i key={star} className={`fa ${starClass} ${colorClass} mr-1`} />;
  });

  return (
    <span className="d-inline-flex align-items-center">
      {stars}
      {showValue && <span className="ml-1 text-muted">({value})</span>}
    </span>
  );
};

export const handleCreateSelectOption = ({
  inputValue,
  options,
  setOptions,
  onSelect,
  valueFormatter = (v) => v,
  labelFormatter = (v) => v,
}) => {
  const trimmedValue = inputValue.trim();
  if (!trimmedValue) return;
  const value = valueFormatter(trimmedValue);
  const label = labelFormatter(trimmedValue);
  // prevent duplicates (case-insensitive)
  const exists = options.some(
    (opt) => opt.value.toLowerCase() === value.toLowerCase(),
  );
  if (exists) {
    onSelect(value);
    return;
  }
  const newOption = { value, label };
  // add new to end
  setOptions((prev) => [...prev, newOption]);
  // auto-select new option
  onSelect(value);
};

export const showAlert = ({
  title = "",
  message = null, // JSX support
  type = "",
  confirmText = "Yes",
  cancelText = "Cancel",
  confirmStyle = "danger",
  cancelStyle = "secondary",
  showCancel = true,
  onConfirm = () => { },
  onCancel = () => { },
  children = null,
  closeOnConfirm = true,
  allowOutsideClick = true,
  showConfirm = true,
  showInput = false,
  inputPlaceholder = "",
  inputRequired = false,
  inputErrorMsg = "This field is required.",
}) => {
  const container = document.createElement("div");
  const root = createRoot(container);
  document.body.appendChild(container);

  const handleClose = () => {
    root.unmount();
    container.remove();
  };

  const AlertWrapper = () => {
    const inputValRef = useRef("");
    const [error, setError] = useState("");

    const handleConfirmClick = async () => {
      const val = inputValRef.current;
      if (showInput && inputRequired && !val.trim()) {
        setError(inputErrorMsg);
        return;
      }

      const shouldClose = await onConfirm(showInput ? val : handleClose, showInput ? handleClose : undefined);

      if (shouldClose !== false && closeOnConfirm) {
        handleClose();
      }
    };

    const handleCancel = () => {
      handleClose();
      onCancel();
    };

    return (
      <ReactBSAlert
        warning={type === "warning"}
        success={type === "success"}
        info={type === "info"}
        title={<p className="small-text mb-0">{title}</p>}
        onConfirm={handleConfirmClick}
        onCancel={handleCancel}
        confirmBtnBsStyle={confirmStyle}
        cancelBtnBsStyle={cancelStyle}
        confirmBtnCssClass="save-btn-sm"
        cancelBtnCssClass="save-btn-sm"
        confirmBtnText={confirmText}
        cancelBtnText={cancelText}
        showCancel={showCancel}
        showConfirm={showConfirm}
        allowOutsideClick={allowOutsideClick}
        btnSize=""
      >
        {message}
        {showInput && (
          <div className="mt-3 text-left">
            <Input
              type="textarea"
              rows={2}
              maxLength={100}
              className={`form-control ${error ? "is-invalid" : ""}`}
              placeholder={inputPlaceholder}
              defaultValue={inputValRef.current}
              onChange={(e) => {
                inputValRef.current = e.target.value;
                if (e.target.value.trim() && error) setError("");
              }}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
        )}
        {typeof children === "function" ? children(handleClose) : children}
      </ReactBSAlert>
    );
  };

  root.render(<AlertWrapper />);
  return handleClose;
};

export const getPasswordStrengthColor = (passwordStrength) => {
  switch (passwordStrength) {
    case "Strong":
      return "text-success";
    case "Medium":
      return "text-warning";
    case "Weak":
    default:
      return "text-danger";
  }
};
export const getPasswordStrength = (password) => {
  let strength = "Weak";
  if (
    password.length >= 8 &&
    /\d/.test(password) &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password)
  ) {
    strength = "Strong";
  } else if (
    password.length >= 8 &&
    (/\d/.test(password) || /[@$!%*?&]/.test(password))
  ) {
    strength = "Medium";
  }
  return strength;
};

// Function to calculate duration between start and end time (12-hour format)
export const getDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return "-";

  const [startHour, startMin] = startTime.split(":").map(Number);
  const [endHour, endMin] = endTime.split(":").map(Number);

  const startTotalMinutes = startHour * 60 + startMin;
  const endTotalMinutes = endHour * 60 + endMin;

  const diffMinutes = endTotalMinutes - startTotalMinutes;

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return `${hours}h ${minutes}m`;
};

export const convertExperienceTextToMonths = (experience) => {
  if (!experience) return 0;
  const parts = experience.split(" "); //split the data or value by space
  // 3 years 2 months
  const years = Number(parts[0]) || 0; // take first value -> convert to number
  //check if months exists -> if exits -> convert to number
  const months =
    parts.includes("months") || parts.includes("month")
      ? Number(parts[2]) || 0
      : 0;

  return years * 12 + months;
};

export const allowOnlyDigits = (e) => {
  if (
    !REGEX.DIGIT_KEY_REGEX.test(e.key) &&
    !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
  ) {
    e.preventDefault();
  }
};

export const toCamelCase = (str) => {
  if (!str) return "";

  return str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const toTitleCase = (str) => {
  if (!str) return "";

  return str
    .split("_")
    .map(
      (word) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(", ");
};

export const addOneYear = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  date.setFullYear(date.getFullYear() + 1);

  return date.toISOString().split("T")[0]; // YYYY-MM-DD
};

//converts 03 Jan 2026 to 2026-01-03
export const humanReadableToInputDate = (dateStr) => {
  if (!dateStr) return "";

  const [day, monthStr, year] = dateStr.split(" ");

  const months = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  const month = months[monthStr];

  return `${year}-${month}-${day.padStart(2, "0")}`;
};

export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

//"31 Aug 2026, 05:48 PM" to "2026-08-31T17:48:00Z"
// Converts a human-readable date and time into the API-compatible datetime format.
export const formatDateTime = (val) => {
  if (!val) return "";
  let dateVal = val;
  if (!(dateVal instanceof Date)) {
    dateVal = new Date(dateVal);
  }
  if (isNaN(dateVal.getTime())) return val;
  return format(dateVal, "yyyy-MM-dd'T'HH:mm:ss'Z'");
};

export const formatText = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formattedPrice = (price) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price || 0);

export const formatTime12Hour = (time) => {
  if (!time) return "-";
  if (typeof time !== "string") return "-";
  
  if (time.toUpperCase().includes("AM") || time.toUpperCase().includes("PM")) {
    return time;
  }
  
  try {
    const formatStr = time.split(":").length === 3 ? "HH:mm:ss" : "HH:mm";
    const parsedDate = parse(time, formatStr, new Date());
    if (isNaN(parsedDate)) {
      return time;
    }
    return format(parsedDate, "hh:mm a");
  } catch (error) {
    return time;
  }
};

export const formatTime24Hour = (time12h) => {
  const [time, modifier] = time12h.trim().split(" ");

  let [hours, minutes] = time.split(":");

  hours = parseInt(hours, 10);

  if (modifier.toUpperCase() === "AM") {
    if (hours === 12) {
      hours = 0;
    }
  } else if (modifier.toUpperCase() === "PM") {
    if (hours !== 12) {
      hours += 12;
    }
  }

  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

// Converts an HH:mm time string into a Date object using today's date.
export const parseTimeStringToDate = (timeString) => {
  if (!timeString) return null;
  const date = new Date();
  const [hours, minutes] = timeString.split(":");
  date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  return date;
};